import { confirmTransactionWithRetry } from '@prediction-market/providers';
import { usePrivy } from '@privy-io/react-auth';
import {
  useSignAndSendTransaction,
  useSignMessage,
  useWallets,
} from '@privy-io/react-auth/solana';
import { SendTransactionOptions } from '@solana/wallet-adapter-base';
import { useWallet } from '@solana/wallet-adapter-react';
import { PublicKey, Transaction } from '@solana/web3.js';
import bs58 from 'bs58';
import { useCallback } from 'react';

import { privyChain } from '@/constants/privy';
import { connection } from '@/constants/solana';
import { sendSolanaTransaction } from '@/lib/send-transaction';

export const useAuthenticatedWallet = () => {
  const { signAndSendTransaction } = useSignAndSendTransaction();
  const { signMessage: privySignMessage } = useSignMessage();
  const { wallets } = useWallets();

  const selectedWallet = wallets[0];
  const { user, logout } = usePrivy();
  const {
    publicKey: walletPublicKey,
    disconnect,
    sendTransaction: walletSendTransaction,
    signMessage: walletSignMessage,
    wallet,
  } = useWallet();
  const publicKey = walletPublicKey
    ? walletPublicKey
    : user?.wallet?.address
      ? new PublicKey(user?.wallet?.address)
      : null;

  const isPrivyUser = !walletPublicKey;

  const isPrivyEmbeddedWallet =
    user?.wallet?.connectorType !== 'solana_adapter';

  const sendTransaction = useCallback(
    async (
      tx: Transaction,
      options?: SendTransactionOptions,
    ): Promise<string> => {
      if (isPrivyUser && selectedWallet) {
        console.log(selectedWallet.address);
        const { blockhash } = await connection.getLatestBlockhash();
        tx.recentBlockhash = blockhash;
        tx.feePayer = new PublicKey(selectedWallet.address);

        tx.signatures = [];

        const encodedTransaction = new Uint8Array(
          tx.serialize({
            requireAllSignatures: false,
            verifySignatures: false,
          }),
        );

        const result = await signAndSendTransaction({
          transaction: encodedTransaction,
          wallet: selectedWallet,
          chain: privyChain,
        });

        const signature = bs58.encode(result.signature);
        await confirmTransactionWithRetry(connection, signature);

        return signature;
      } else {
        return await sendSolanaTransaction(
          tx,
          wallet,
          walletSendTransaction,
          options,
        );
      }
    },
    [
      isPrivyUser,
      selectedWallet,
      signAndSendTransaction,
      wallet,
      walletSendTransaction,
    ],
  );

  const signMessage = useCallback(
    async (message: string): Promise<Uint8Array> => {
      const messageBytes = new TextEncoder().encode(message);

      if (isPrivyUser && selectedWallet) {
        const result = await privySignMessage({
          message: messageBytes,
          wallet: selectedWallet,
          options: { uiOptions: { title: 'Sign Authentication Message' } },
        });
        return result.signature;
      } else if (walletSignMessage) {
        return await walletSignMessage(messageBytes);
      } else {
        throw new Error('No wallet available for signing messages');
      }
    },
    [isPrivyUser, selectedWallet, privySignMessage, walletSignMessage],
  );

  const disconnectWallet = useCallback(async () => {
    try {
      await Promise.all([logout(), disconnect()]);
    } catch {
      // ignore
    }
  }, [logout, disconnect]);

  return {
    publicKey,
    isPrivyUser,
    isPrivyEmbeddedWallet,
    sendTransaction,
    signMessage,
    disconnectWallet,
  };
};
